import React, { Suspense, lazy } from 'react';
import { compose } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import WidgetBox, { Props as WidgetBoxProps } from '../WidgetBox';
import { withModuleData, WithModuleData } from '../../_hoc/withModuleData';
import reduxConnectContainer from './ExhibitionistBox.container';
import ConfigPopover from '../../_partials/ConfigPopover';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import OverlayContext from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard/Overlay/OverlayContext';
import { GallectionType } from '../../../../../types/gallection';

const ExhibitionistEditForm = lazy(() =>
  import(/* webpackChunkName: "owner" */ './ExhibitionistEditForm')
);

export enum ExhibitionistBoxType {
  TALL = 'tall',
  STRIP = 'strip',
}

export interface Props {
  moduleId: number;
  exhibitionistType: ExhibitionistBoxType;
  title: string;
  seeAllUrl?: string;
  isEmpty?: boolean;
  emptyView?: React.ReactNode;
  headerExtras?: WidgetBoxProps['headerExtras'];
  children: (boxProps) => React.ReactNode;
}

const defaultProps = {
  placement: 'bottom',
};

export type ExhibitionistBoxProps = Props &
  WidgetBoxProps &
  WithModuleData &
  WithTranslation;

const enhance = compose(
  reduxConnectContainer,
  withModuleData,
  withTranslation()
);
class ExhibitionistBox extends React.Component<ExhibitionistBoxProps> {
  static defaultProps = defaultProps;
  render() {
    const {
      t,
      isHeaderStickyOnMobile,
      widgetMoveUp,
      widgetMoveDown,
      widgetRemove,
      headerExtras,
      emptyView,
      children,
      moduleData: { sourceType, viewMode, slideImageFit },
      isCurrentUserProfileOwner,
      section,
      zone,
      ...props
    } = this.props;

    const isCollection = sourceType === GallectionType.COLLECTION;
    const defaultTitle = this.getDefaultTitle(sourceType);

    return (
      <WidgetBox
        section={section}
        zone={zone}
        header={props.title || defaultTitle}
        headerExtras={headerExtras}
        isHeaderStickyOnMobile={isHeaderStickyOnMobile}
        hasMore={props.hasMore}
        seeAllUrl={props.seeAllUrl}
        widgetMenuRenderer={this.renderEditMenu}
        moduleId={props.moduleId}
        widgetMoveUp={widgetMoveUp}
        widgetMoveDown={widgetMoveDown}
        widgetRemove={widgetRemove}
        isEmpty={props.isEmpty}
        emptyView={emptyView}
      >
        <OverlayContext.Provider
          value={{
            showAuthorTooltip: isCollection,
            showPermanentIconWhenFaved: !(
              isCollection && isCurrentUserProfileOwner
            ),
          }}
        >
          {children({ ...props, viewMode, slideImageFit })}
        </OverlayContext.Provider>
      </WidgetBox>
    );
  }

  getDefaultTitle = (sourceType: GallectionType) => {
    const { t } = this.props;

    if (sourceType === GallectionType.GALLERY) {
      return t(`widgets.exhibitionist.source.gallery`);
    } else {
      return t(`widgets.exhibitionist.source.collection`);
    }
  };

  renderEditMenu = closeMenu => {
    return (
      <ConfigPopover width={266} onClose={closeMenu} withPaddings={false}>
        <Suspense fallback={<LoadingIndicator />}>
          <ExhibitionistEditForm
            moduleId={this.props.moduleId}
            exhibitionistType={this.props.exhibitionistType}
            close={closeMenu}
          />
        </Suspense>
      </ConfigPopover>
    );
  };
}

export default enhance(ExhibitionistBox) as React.ComponentType<Props>;
